(() => {
  const component = document.getElementById('ag-assistance');
  if (!component) return;
  const launcher = component.querySelector('.ag-assistance__launcher');
  const options = component.querySelector('#ag-assistance-options');
  const aiChoice = component.querySelector('[data-ag-open-ai]');
  const status = component.querySelector('[data-ag-ai-status]');
  const fallback = component.querySelector('[data-ag-contact-fallback]');
  const close = component.querySelector('.ag-assistance__close');
  const nativePopover = typeof options.showPopover === 'function';
  const observedRoots = new WeakSet();
  let nativeButton = null;
  let poll = null;
  let pollCount = 0;
  let resolving = false;
  let expandedBefore = false;
  let openedByUs = false;
  let availability = 'pending';
  const isOptionsOpen = () => nativePopover ? options.matches(':popover-open') : !options.hidden;
  const hideOptions = () => {
    if (nativePopover && isOptionsOpen()) options.hidePopover();
    else if (!nativePopover) options.hidden = true;
    launcher.setAttribute('aria-expanded', 'false');
  };
  const showOptions = () => {
    if (nativePopover && !isOptionsOpen()) options.showPopover();
    else if (!nativePopover) options.hidden = false;
    launcher.setAttribute('aria-expanded', 'true');
  };
  const updateAvailability = value => {
    availability = value;
    status.hidden = value === 'ready';
    fallback.hidden = value !== 'unavailable';
    const text = value === 'pending' ? status.dataset.pending : value === 'unavailable' ? status.dataset.unavailable : '';
    if (status.textContent !== text) status.textContent = text;
  };
  const overlapsAction = (launcherRect, actionRect, gap = 8) => actionRect.width > 0 && actionRect.height > 0 &&
    launcherRect.left < actionRect.right + gap && launcherRect.right > actionRect.left - gap &&
    launcherRect.top < actionRect.bottom + gap && launcherRect.bottom > actionRect.top - gap;
  let positionScheduled = false;
  const positionLauncher = () => {
    component.classList.remove('ag-assistance--avoid-action');
    if (!window.matchMedia('(max-width:749px)').matches || launcher.hidden || !launcher.isConnected) return;
    if (getComputedStyle(launcher).visibility === 'hidden') return;
    const defaultRect = launcher.getBoundingClientRect();
    if (!defaultRect.width || !defaultRect.height) return;
    const actionRects = [...document.querySelectorAll('.ag-story-hero .ag26-actions,.product-form__submit,.cart__checkout-button')].map(action => action.getBoundingClientRect()).filter(rect => rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth);
    if (!actionRects.some(rect => overlapsAction(defaultRect, rect))) return;
    const headerBottoms = [...document.querySelectorAll('.section-header,.shopify-section-header-sticky,.header-wrapper,header.header')].map(header => header.getBoundingClientRect()).filter(rect => rect.bottom > 0 && rect.bottom <= Math.min(160, window.innerHeight * .4)).map(rect => rect.bottom + 8);
    const dockTop = Math.max(78, ...headerBottoms);
    component.style.setProperty('--ag-assistance-dock-top', `${Math.round(dockTop)}px`);
    component.style.setProperty('--ag-assistance-launcher-height', `${Math.ceil(defaultRect.height)}px`);
    component.classList.add('ag-assistance--avoid-action');
  };
  const queuePosition = () => {
    if (positionScheduled) return;
    positionScheduled = true;
    requestAnimationFrame(() => {
      positionScheduled = false;
      positionLauncher();
    });
  };
  const syncNativeState = () => {
    const expanded = nativeButton?.isConnected && nativeButton.getAttribute('aria-expanded') === 'true';
    launcher.hidden = Boolean(expanded);
    if (expanded) {
      hideOptions();
      updateAvailability('ready');
    } else if (expandedBefore && openedByUs) {
      openedByUs = false;
      launcher.focus({ preventScroll: true });
    }
    expandedBefore = Boolean(expanded);
    queuePosition();
  };
  const scheduleResolve = () => {
    if (resolving) return;
    resolving = true;
    requestAnimationFrame(() => {
      resolving = false;
      resolveNative();
    });
  };
  const observeRoot = root => {
    if (!root || observedRoots.has(root)) return;
    observedRoots.add(root);
    new MutationObserver(scheduleResolve).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded', 'disabled']
    });
  };
  const resolveNative = () => {
    const host = document.querySelector('shopify-chat');
    const hostRoot = host?.shadowRoot;
    observeRoot(hostRoot);
    const runtime = hostRoot?.querySelector('shopify-agent-runtime');
    const runtimeRoot = runtime?.shadowRoot;
    observeRoot(runtimeRoot);
    const button = runtimeRoot?.querySelector('button.activator[aria-expanded]');
    if (!button) {
      host?.removeAttribute('data-ag-native-ready');
      nativeButton = null;
      launcher.hidden = false;
      return false;
    }
    if (!runtimeRoot.querySelector('#ag-assistance-activator-style')) {
      const style = document.createElement('style');
      style.id = 'ag-assistance-activator-style';
      style.textContent = 'button.activator{display:none!important}';
      runtimeRoot.append(style);
    }
    if (!host.hasAttribute('data-ag-native-ready')) host.setAttribute('data-ag-native-ready', '');
    const changed = nativeButton !== button;
    nativeButton = button;
    if (changed || availability === 'pending') updateAvailability(button.disabled ? 'pending' : 'ready');
    syncNativeState();
    if (poll) {
      clearInterval(poll);
      poll = null;
    }
    return true;
  };
  if (nativePopover) {
    options.addEventListener('toggle', event => launcher.setAttribute('aria-expanded', String(event.newState === 'open')));
  } else {
    options.hidden = true;
    launcher.addEventListener('click', () => isOptionsOpen() ? hideOptions() : showOptions());
    close.addEventListener('click', hideOptions);
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && isOptionsOpen()) {
        hideOptions();
        launcher.focus({ preventScroll: true });
      }
    });
    document.addEventListener('click', event => {
      if (!component.contains(event.target)) hideOptions();
    });
  }
  aiChoice.addEventListener('click', event => {
    event.stopPropagation();
    if (!resolveNative() || nativeButton.disabled) {
      updateAvailability('unavailable');
      return;
    }
    updateAvailability('ready');
    openedByUs = true;
    hideOptions();
    window.setTimeout(() => {
      if (!resolveNative() || nativeButton.disabled) {
        openedByUs = false;
        updateAvailability('unavailable');
        showOptions();
        return;
      }
      nativeButton.click();
      syncNativeState();
      window.setTimeout(() => {
        syncNativeState();
        if (!expandedBefore && openedByUs) {
          openedByUs = false;
          updateAvailability('unavailable');
          showOptions();
          aiChoice.focus({ preventScroll: true });
        }
      }, 1200);
    }, 0);
  });
  new MutationObserver(() => {
    scheduleResolve();
    queuePosition();
  }).observe(document.body, { childList: true, characterData: true, subtree: true });
  window.addEventListener('scroll', queuePosition, { passive: true, capture: true });
  window.addEventListener('resize', queuePosition, { passive: true });
  window.addEventListener('load', queuePosition, { once: true });
  document.addEventListener('shopify:section:load', queuePosition);
  document.fonts?.ready.then(queuePosition);
  queuePosition();
  if (!resolveNative()) {
    poll = window.setInterval(() => {
      pollCount += 1;
      if (resolveNative()) return;
      if (pollCount >= 40) {
        clearInterval(poll);
        poll = null;
        updateAvailability('unavailable');
      }
    }, 500);
  }
  for (const tag of ['shopify-chat', 'shopify-agent-runtime']) {
    customElements.whenDefined(tag).then(resolveNative);
  }
})();
